import { useQuery } from '@apollo/client'
import { 
  CubeTransparentIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline'
import { ProductVariant } from '../types/product'
import { GET_BUNDLE_GROUPS_BY_VARIANT } from '../graphql/product-queries'
import LoadingSpinner from '../components/LoadingSpinner'

interface VariantBundleGroupsTabProps {
  variant: ProductVariant
  productId: number
}

const VariantBundleGroupsTab = ({ variant }: VariantBundleGroupsTabProps) => {

  const { data: bundleGroupsData, loading, error } = useQuery(GET_BUNDLE_GROUPS_BY_VARIANT, {
    variables: { variantId: Number(variant.id) },
    skip: !variant.id || variant.id === null || variant.id === undefined
  })

  const bundleGroups = bundleGroupsData?.bundleGroupsByEligibleVariant || []

  // Since the query already filters by variant, all returned groups are relevant
  const relevantBundleGroups = bundleGroups

  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`
  }

  const formatPriceDelta = (cents: number) => {
    const sign = cents >= 0 ? '+' : ''
    return `${sign}€${(cents / 100).toFixed(2)}`
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading bundle groups</h3>
        <p className="mt-1 text-sm text-gray-500">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Grouped Meals</h2>
          <p className="mt-1 text-sm text-gray-500">
            Grouped meals where this variant is available as an option
          </p>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-5 w-5 text-blue-400" />
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">
              Grouped Meal Participation
            </h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>
                This variant can be selected as an option in grouped meals. 
                Grouped meals allow customers to combine multiple items with special pricing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {relevantBundleGroups.length === 0 ? (
        /* Empty State */
        <div className="text-center py-12">
          <CubeTransparentIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Not part of any grouped meals</h3>
          <p className="mt-1 text-sm text-gray-500">
            This variant is not currently available as an option in any grouped meals.
          </p>
        </div>
      ) : (
        /* Grouped Meals List */
        <div className="space-y-4">
          {relevantBundleGroups.map((bundleGroup: any) => {
            const variantOption = bundleGroup.options?.find((option: any) => 
              option.eligible_variant.id === variant.id
            )

            return (
              <div 
                key={bundleGroup.id} 
                className="card"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <CubeTransparentIcon className="h-5 w-5 text-gray-400 mr-3" />
                        <div>
                          <h3 className="text-lg font-medium text-gray-900">
                            {bundleGroup.name}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            Part of {bundleGroup.bundle_product?.name} grouped meal
                          </p>
                        </div>
                      </div>
                      
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Required Items</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {bundleGroup.required_count} item{bundleGroup.required_count !== 1 ? 's' : ''}
                          </dd>
                        </div>
                        
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Price Delta</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {variantOption ? formatPriceDelta(variantOption.price_delta_cents) : 'N/A'}
                          </dd>
                        </div>
                        
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Available Options</dt>
                          <dd className="mt-1 text-sm text-gray-900">
                            {bundleGroup.options?.length || 0}
                          </dd>
                        </div>
                      </div>

                      {/* Price Information */}
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-sm font-medium text-gray-700">Base Price:</span>
                            <span className="ml-2 text-sm text-gray-900">
                              {formatPrice(variant.price_cents)}
                            </span>
                          </div>
                          {variantOption && variantOption.price_delta_cents !== 0 && (
                            <div>
                              <span className="text-sm font-medium text-gray-700">Grouped Meal Price:</span>
                              <span className="ml-2 text-sm text-gray-900">
                                {formatPrice(variant.price_cents + variantOption.price_delta_cents)}
                              </span>
                            </div>
                          )}
                        </div>
                        {variantOption && variantOption.price_delta_cents !== 0 && (
                          <div className="mt-1 text-xs text-gray-500">
                            {variantOption.price_delta_cents > 0 
                              ? `Additional cost of ${formatPriceDelta(variantOption.price_delta_cents)}`
                              : `Discount of ${formatPriceDelta(Math.abs(variantOption.price_delta_cents))}`
                            }
                          </div>
                        )}
                      </div>

                      {/* Other Options Preview */}
                      {bundleGroup.options && bundleGroup.options.length > 1 && (
                        <div className="mt-4">
                          <dt className="text-sm font-medium text-gray-500 mb-2">Other Meal Options</dt>
                          <div className="flex flex-wrap gap-2">
                            {bundleGroup.options
                              .filter((option: any) => option.eligible_variant.id !== variant.id)
                              .slice(0, 5)
                              .map((option: any) => (
                                <span
                                  key={option.id}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                                >
                                  {option.eligible_variant.name}
                                  {option.price_delta_cents !== 0 && (
                                    <span className="ml-1 text-gray-500">
                                      ({formatPriceDelta(option.price_delta_cents)})
                                    </span>
                                  )}
                                </span>
                              ))}
                            {bundleGroup.options.filter((option: any) => option.eligible_variant.id !== variant.id).length > 5 && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                +{bundleGroup.options.filter((option: any) => option.eligible_variant.id !== variant.id).length - 5} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default VariantBundleGroupsTab
